import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Lightbulb, Zap } from 'lucide-react';
import { LatexRenderer } from '../../ui/LatexRenderer';
import { Card } from '../../ui/Card';
interface ExplanationCardProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact';
}
// Helper to render explanation steps
const ExplanationSteps = ({
  text
}: {
  text: string;
}) => {
  const steps = text.split('\n').filter(line => line.trim().length > 0);
  if (steps.length <= 1) {
    return <div className="text-lg leading-relaxed" data-id="element-590">
        <LatexRenderer content={text} data-id="element-591" />
      </div>;
  }
  return <div className="space-y-3" data-id="element-592">
      {steps.map((step, idx) => <div key={idx} className="flex gap-4 items-start" data-id="element-593">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/60 flex items-center justify-center font-bold text-base border-2 border-current opacity-90 shadow-sm mt-0.5" data-id="element-594">
            {idx + 1}
          </div>
          <div className="flex-1 text-lg leading-relaxed pt-0.5" data-id="element-595">
            <LatexRenderer content={step} data-id="element-596" />
          </div>
        </div>)}
    </div>;
};
/**
 * Displays explanations for correct and incorrect answers
 * Supports both immediate feedback and review modes
 */
export function ExplanationCard({
  explanation,
  distractorExplanation,
  isCorrect,
  mode = 'full'
}: ExplanationCardProps) {
  if (mode === 'compact') {
    // Compact mode for review lists
    return <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200" data-id="element-597">
        <div className="flex items-center gap-2 font-bold text-indigo-800 mb-2" data-id="element-598">
          <Zap className="w-4 h-4" data-id="element-599" />
          Uitleg
        </div>
        <div className="text-indigo-900 text-sm leading-relaxed" data-id="element-600">
          <LatexRenderer content={explanation} data-id="element-601" />
        </div>
      </div>;
  }
  // Full mode for immediate feedback
  return <div className="space-y-6" data-id="element-602">
      {/* WRONG ANSWER SECTION */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-603">
          <Card className="border-2 border-rose-200 bg-rose-50 overflow-hidden" data-id="element-604">
            <div className="bg-rose-100/50 p-4 border-b border-rose-200 flex items-center gap-3" data-id="element-605">
              <div className="bg-rose-100 p-2 rounded-xl" data-id="element-606">
                <AlertCircle className="w-8 h-8 text-rose-600" data-id="element-607" />
              </div>
              <h3 className="text-xl font-bold text-rose-900" data-id="element-608">
                Waarom is dit niet goed?
              </h3>
            </div>
            <div className="p-6" data-id="element-609">
              <div className="text-rose-900 font-medium" data-id="element-610">
                <ExplanationSteps text={distractorExplanation} data-id="element-611" />
              </div>
            </div>
          </Card>
        </motion.div>}

      {/* CORRECT ANSWER SECTION */}
      <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-612">
        <Card className="border-2 border-emerald-200 bg-emerald-50 overflow-hidden shadow-lg" data-id="element-613">
          <div className="bg-emerald-100/50 p-4 border-b border-emerald-200 flex items-center gap-3" data-id="element-614">
            <div className="bg-emerald-100 p-2 rounded-xl" data-id="element-615">
              <Lightbulb className="w-8 h-8 text-emerald-600" data-id="element-616" />
            </div>
            <h3 className="text-xl font-bold text-emerald-900" data-id="element-617">
              {isCorrect ? 'Waarom dit klopt' : 'Hoe het wel moet'}
            </h3>
          </div>
          <div className="p-6" data-id="element-618">
            <div className="text-emerald-900 font-medium" data-id="element-619">
              <ExplanationSteps text={explanation || 'Goed gedaan! Je hebt de juiste strategie gebruikt.'} data-id="element-620" />
            </div>
          </div>
        </Card>
      </motion.div>
    </div>;
}